/**
 * CasaMapa search state: URL ↔ API query (Milestone 3).
 * Canonical prices are always COP. Language-neutral URL params.
 */

import {
  normalizeCasaMapaMunicipality,
  normalizeCasaMapaMunicipalityList,
} from "./casaMapaMunicipality";
import { normalizeCasaMapaNeighborhood } from "./casaMapaNeighborhood";
import { toCasaMapaApiSort } from "./casaMapaRecommendationRank";
import { applyCasaMapaFreeTextQueryToSearchState } from "./casaMapaFreeTextQuery";

export const CASAMAPA_SEARCH_SORT = Object.freeze({
  RECOMMENDED: "recommended",
  RECENT: "recent",
  PRICE_ASC: "price_asc",
  PRICE_DESC: "price_desc",
  BEDROOMS_DESC: "bedrooms_desc",
  RELEVANCE: "relevance",
});

export const CASAMAPA_SEARCH_SORT_VALUES = Object.freeze(
  Object.values(CASAMAPA_SEARCH_SORT)
);

export const CASAMAPA_DEFAULT_PAGE_SIZE = 24;
/** Max property cards shown for the current map camera in split view. */
export const CASAMAPA_VIEWPORT_CARD_LIMIT = 30;
/** Matches casamapa-inventory query profile max_limit for denser map coverage. */
export const CASAMAPA_MAX_PAGE_SIZE = 100;

/** Medellín metro default viewport (west, south, east, north). */
export const CASAMAPA_DEFAULT_BOUNDS = Object.freeze({
  west: -75.72,
  south: 6.05,
  east: -75.45,
  north: 6.38,
});

export const CASAMAPA_DEFAULT_CENTER = Object.freeze([-75.58, 6.22]);
export const CASAMAPA_DEFAULT_ZOOM = 11;

export function createDefaultCasaMapaSearchState() {
  return {
    listingType: "rent",
    propertyTypes: [],
    priceMinCop: null,
    priceMaxCop: null,
    bedroomsMin: null,
    bathroomsMin: null,
    parkingMin: null,
    constructedAreaMinM2: null,
    constructedAreaMaxM2: null,
    estratos: [],
    municipalities: [],
    /** @deprecated Prefer municipalities[]; kept as first selected for older callers. */
    municipality: null,
    neighborhood: null,
    q: null,
    bounds: null,
    sort: CASAMAPA_SEARCH_SORT.RECOMMENDED,
    page: 1,
    pageSize: CASAMAPA_DEFAULT_PAGE_SIZE,
    view: "split",
  };
}

function pickNumber(value) {
  if (value == null || value === "") {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function pickPositiveInt(value) {
  const n = pickNumber(value);
  if (n == null || n < 0) {
    return null;
  }
  return Math.floor(n);
}

function parseCsv(value) {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizePropertyType(value) {
  const raw = String(value || "").toLowerCase().trim();
  if (!raw) {
    return null;
  }
  const aliases = {
    apartamento: "apartment",
    apartment: "apartment",
    casa: "house",
    house: "house",
    apartaestudio: "studio",
    studio: "studio",
    penthouse: "penthouse",
    duplex: "duplex",
    dúplex: "duplex",
    finca: "farm",
    lote: "lot",
    lot: "lot",
    land: "lot",
    terreno: "lot",
    country_house: "country_house",
    oficina: "office",
    office: "office",
  };
  return aliases[raw] || raw;
}

function normalizeListingType(value) {
  const raw = String(value || "").toLowerCase().trim();
  if (raw === "sale" || raw === "venta" || raw === "buy" || raw === "comprar") {
    return "sale";
  }
  if (raw === "rent" || raw === "arriendo" || raw === "alquiler" || raw === "arrendar") {
    return "rent";
  }
  return null;
}

function normalizeSort(value) {
  const raw = String(value || "").toLowerCase().trim();
  if (CASAMAPA_SEARCH_SORT_VALUES.includes(raw)) {
    return raw;
  }
  if (raw === "newest") {
    return CASAMAPA_SEARCH_SORT.RECENT;
  }
  return CASAMAPA_SEARCH_SORT.RECOMMENDED;
}

function normalizeView(value) {
  const raw = String(value || "").toLowerCase().trim();
  if (raw === "list" || raw === "map" || raw === "split") {
    return raw;
  }
  return "split";
}

/** Phones (portrait and most landscapes) open the search as a list. */
export const CASAMAPA_PHONE_SEARCH_MEDIA_QUERY = "(max-width: 768px)";

/**
 * View when the URL does not name one. An explicit `view` param always wins.
 */
export function resolveCasaMapaSearchView(parsedView, { viewSpecified = false, isPhone = false } = {}) {
  if (viewSpecified) {
    return normalizeView(parsedView);
  }
  return isPhone ? "list" : "split";
}

function clampPage(value) {
  const n = pickPositiveInt(value);
  return n && n > 0 ? n : 1;
}

function clampPageSize(value) {
  const n = pickPositiveInt(value);
  if (!n) {
    return CASAMAPA_DEFAULT_PAGE_SIZE;
  }
  return Math.min(CASAMAPA_MAX_PAGE_SIZE, Math.max(1, n));
}

/**
 * Parse bbox string `west,south,east,north` or object.
 */
export function parseCasaMapaBounds(value) {
  if (!value) {
    return null;
  }
  if (typeof value === "object" && value.west != null) {
    const west = Number(value.west);
    const south = Number(value.south);
    const east = Number(value.east);
    const north = Number(value.north);
    if ([west, south, east, north].every(Number.isFinite) && west < east && south < north) {
      return { west, south, east, north };
    }
    return null;
  }
  const parts = String(value)
    .split(",")
    .map((part) => Number(part.trim()));
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) {
    return null;
  }
  const [west, south, east, north] = parts;
  if (!(west < east && south < north)) {
    return null;
  }
  return { west, south, east, north };
}

export function serializeCasaMapaBounds(bounds) {
  const parsed = parseCasaMapaBounds(bounds);
  if (!parsed) {
    return null;
  }
  return [parsed.west, parsed.south, parsed.east, parsed.north]
    .map((n) => {
      const rounded = Math.round(n * 1e6) / 1e6;
      return String(rounded);
    })
    .join(",");
}

function toSearchParams(input) {
  if (input instanceof URLSearchParams) {
    return input;
  }
  if (typeof input === "string") {
    return new URLSearchParams(input.replace(/^\?/, ""));
  }
  if (input && typeof input === "object") {
    const sp = new URLSearchParams();
    Object.entries(input).forEach(([key, value]) => {
      if (value == null || value === "") {
        return;
      }
      if (Array.isArray(value)) {
        if (value.length) {
          sp.set(key, value.join(","));
        }
        return;
      }
      sp.set(key, String(value));
    });
    return sp;
  }
  return new URLSearchParams();
}

/**
 * @param {URLSearchParams|string|object} input
 */
export function parseCasaMapaSearchParams(input) {
  const defaults = createDefaultCasaMapaSearchState();
  const sp = toSearchParams(input);

  const state = { ...defaults };
  state.listingType = normalizeListingType(
    sp.get("listingType") || sp.get("operacion") || sp.get("transactionType")
  );
  state.propertyTypes = parseCsv(sp.get("propertyType") || sp.get("propertyTypes") || sp.get("tipo"))
    .map(normalizePropertyType)
    .filter(Boolean);
  state.priceMinCop = pickPositiveInt(sp.get("minPrice") || sp.get("priceMinCop") || sp.get("precioMin"));
  state.priceMaxCop = pickPositiveInt(sp.get("maxPrice") || sp.get("priceMaxCop") || sp.get("precioMax"));
  state.bedroomsMin = pickPositiveInt(sp.get("bedrooms") || sp.get("minBedrooms") || sp.get("habitaciones"));
  state.bathroomsMin = pickPositiveInt(sp.get("bathrooms") || sp.get("minBathrooms") || sp.get("banos"));
  state.parkingMin = pickPositiveInt(sp.get("parking") || sp.get("minParking") || sp.get("parqueaderos"));
  state.constructedAreaMinM2 = pickPositiveInt(
    sp.get("minArea") || sp.get("minAreaM2") || sp.get("areaMin")
  );
  state.constructedAreaMaxM2 = pickPositiveInt(
    sp.get("maxArea") || sp.get("maxAreaM2") || sp.get("areaMax")
  );
  state.estratos = parseCsv(sp.get("estrato") || sp.get("estratos"))
    .map((item) => pickPositiveInt(item))
    .filter((n) => n != null && n >= 1 && n <= 6);
  const municipalities = normalizeCasaMapaMunicipalityList(
    parseCsv(sp.get("municipality") || sp.get("municipalities") || sp.get("municipio"))
  );
  state.municipalities = municipalities;
  state.municipality = municipalities[0] || null;
  const neighborhood = sp.get("neighborhood") || sp.get("barrio");
  state.neighborhood = normalizeCasaMapaNeighborhood(neighborhood);
  const q = sp.get("q") || sp.get("search") || sp.get("keyword");
  state.q = q?.trim() || null;
  state.bounds = parseCasaMapaBounds(sp.get("bbox") || sp.get("bounds"));
  state.sort = normalizeSort(sp.get("sort"));
  if (state.q && !sp.get("sort")) {
    state.sort = CASAMAPA_SEARCH_SORT.RELEVANCE;
  }
  state.page = clampPage(sp.get("page"));
  state.pageSize = clampPageSize(sp.get("limit") || sp.get("pageSize"));
  state.view = normalizeView(sp.get("view"));

  // Free-text `q` → structured filters (property type, municipality, beds, …).
  // Explicit URL filters always win; matched tokens are stripped from `q`.
  const expanded = applyCasaMapaFreeTextQueryToSearchState(state);
  if (expanded.q && !sp.get("sort") && expanded.q !== state.q) {
    expanded.sort = CASAMAPA_SEARCH_SORT.RELEVANCE;
  }
  if (!expanded.q && !sp.get("sort") && state.q && !sp.get("propertyType") && !sp.get("municipality")) {
    // Fully consumed free-text: fall back to recommended browse sort.
    expanded.sort = defaults.sort;
  }
  return expanded;
}

/**
 * Serialize search state to URLSearchParams (omit defaults / nulls).
 */
export function serializeCasaMapaSearchParams(state = {}) {
  const defaults = createDefaultCasaMapaSearchState();
  const next = new URLSearchParams();
  const listingType = normalizeListingType(state.listingType);
  if (listingType) {
    next.set("listingType", listingType);
  }
  const propertyTypes = Array.isArray(state.propertyTypes)
    ? state.propertyTypes.filter(Boolean)
    : parseCsv(state.propertyTypes);
  if (propertyTypes.length) {
    next.set("propertyType", propertyTypes.join(","));
  }
  if (state.priceMinCop != null && state.priceMinCop > 0) {
    next.set("minPrice", String(Math.round(state.priceMinCop)));
  }
  if (state.priceMaxCop != null && state.priceMaxCop > 0) {
    next.set("maxPrice", String(Math.round(state.priceMaxCop)));
  }
  if (state.bedroomsMin != null && state.bedroomsMin > 0) {
    next.set("bedrooms", String(state.bedroomsMin));
  }
  if (state.bathroomsMin != null && state.bathroomsMin > 0) {
    next.set("bathrooms", String(state.bathroomsMin));
  }
  if (state.parkingMin != null && state.parkingMin > 0) {
    next.set("parking", String(state.parkingMin));
  }
  if (state.constructedAreaMinM2 != null && state.constructedAreaMinM2 > 0) {
    next.set("minArea", String(state.constructedAreaMinM2));
  }
  if (state.constructedAreaMaxM2 != null && state.constructedAreaMaxM2 > 0) {
    next.set("maxArea", String(state.constructedAreaMaxM2));
  }
  if (Array.isArray(state.estratos) && state.estratos.length) {
    next.set("estrato", state.estratos.join(","));
  }
  const municipalities = normalizeCasaMapaMunicipalityList(
    Array.isArray(state.municipalities) && state.municipalities.length
      ? state.municipalities
      : state.municipality
        ? [state.municipality]
        : []
  );
  if (municipalities.length) {
    next.set("municipality", municipalities.join(","));
  }
  if (state.neighborhood) {
    const neighborhood = normalizeCasaMapaNeighborhood(state.neighborhood) || state.neighborhood;
    next.set("neighborhood", String(neighborhood));
  }
  const q = typeof state.q === "string" ? state.q.trim() : "";
  if (q) {
    next.set("q", q);
  }
  const bbox = serializeCasaMapaBounds(state.bounds);
  if (bbox) {
    next.set("bbox", bbox);
  }
  const hasExplicitSort = state.sort != null && String(state.sort).trim() !== "";
  const sort = hasExplicitSort
    ? normalizeSort(state.sort)
    : q
      ? CASAMAPA_SEARCH_SORT.RELEVANCE
      : defaults.sort;
  // With free-text `q`, relevance is the implied default (omit from URL).
  // Without `q`, recommended is the implied default.
  const impliedDefaultSort = q ? CASAMAPA_SEARCH_SORT.RELEVANCE : defaults.sort;
  if (sort !== impliedDefaultSort) {
    next.set("sort", sort);
  }
  const page = clampPage(state.page);
  if (page > 1) {
    next.set("page", String(page));
  }
  const pageSize = clampPageSize(state.pageSize);
  if (pageSize !== defaults.pageSize) {
    next.set("limit", String(pageSize));
  }
  const view = normalizeView(state.view);
  if (view !== defaults.view) {
    next.set("view", view);
  }
  return next;
}

/**
 * Map search state → public listings API query (profile added by caller).
 */
export function buildCasaMapaListingsQuery(state = {}, { profile } = {}) {
  const parsed = {
    ...createDefaultCasaMapaSearchState(),
    ...state,
    listingType: normalizeListingType(state.listingType),
    sort: normalizeSort(state.sort),
    page: clampPage(state.page),
    pageSize: clampPageSize(state.pageSize),
    bounds: parseCasaMapaBounds(state.bounds),
  };

  const params = {
    page: String(parsed.page),
    limit: String(parsed.pageSize),
    sort: toCasaMapaApiSort(parsed.sort, { q: parsed.q }),
  };

  if (profile) {
    params.profile = profile;
  }
  if (parsed.listingType) {
    params.listingType = parsed.listingType;
  }
  const propertyTypes = Array.isArray(parsed.propertyTypes)
    ? parsed.propertyTypes.filter(Boolean)
    : [];
  if (propertyTypes.length) {
    params.propertyType = propertyTypes.join(",");
  }
  if (parsed.priceMinCop != null && parsed.priceMinCop > 0) {
    params.minPrice = String(Math.round(parsed.priceMinCop));
  }
  if (parsed.priceMaxCop != null && parsed.priceMaxCop > 0) {
    params.maxPrice = String(Math.round(parsed.priceMaxCop));
  }
  if (parsed.bedroomsMin != null && parsed.bedroomsMin > 0) {
    params.bedrooms = String(parsed.bedroomsMin);
  }
  if (parsed.bathroomsMin != null && parsed.bathroomsMin > 0) {
    params.bathrooms = String(parsed.bathroomsMin);
  }
  if (parsed.parkingMin != null && parsed.parkingMin > 0) {
    params.minParking = String(parsed.parkingMin);
  }
  if (parsed.constructedAreaMinM2 != null && parsed.constructedAreaMinM2 > 0) {
    params.minArea = String(parsed.constructedAreaMinM2);
  }
  if (parsed.constructedAreaMaxM2 != null && parsed.constructedAreaMaxM2 > 0) {
    params.maxArea = String(parsed.constructedAreaMaxM2);
  }
  const municipalities = normalizeCasaMapaMunicipalityList(
    Array.isArray(parsed.municipalities) && parsed.municipalities.length
      ? parsed.municipalities
      : parsed.municipality
        ? [parsed.municipality]
        : []
  );
  if (municipalities.length) {
    params.municipality = municipalities.join(",");
  }
  if (parsed.neighborhood) {
    params.neighborhood = normalizeCasaMapaNeighborhood(parsed.neighborhood) || String(parsed.neighborhood);
  }
  const q = typeof parsed.q === "string" ? parsed.q.trim() : "";
  if (q) {
    params.q = q;
  }
  const bbox = serializeCasaMapaBounds(parsed.bounds);
  if (bbox) {
    params.bbox = bbox;
  }

  return params;
}

export function clearCasaMapaSearchFilters(state = {}) {
  return {
    ...createDefaultCasaMapaSearchState(),
    view: normalizeView(state.view),
    sort: normalizeSort(state.sort),
    bounds: state.bounds || null,
  };
}

export function countActiveCasaMapaFilters(state = {}) {
  let count = 0;
  if (state.listingType && state.listingType !== "rent") count += 1;
  if (Array.isArray(state.propertyTypes) && state.propertyTypes.length) count += 1;
  if (state.priceMinCop != null) count += 1;
  if (state.priceMaxCop != null) count += 1;
  if (state.bedroomsMin != null) count += 1;
  if (state.bathroomsMin != null) count += 1;
  if (state.parkingMin != null) count += 1;
  if (state.constructedAreaMinM2 != null) count += 1;
  if (state.constructedAreaMaxM2 != null) count += 1;
  if (Array.isArray(state.estratos) && state.estratos.length) count += 1;
  if (
    (Array.isArray(state.municipalities) && state.municipalities.length) ||
    state.municipality
  ) {
    count += 1;
  }
  if (state.neighborhood) count += 1;
  if (typeof state.q === "string" && state.q.trim()) count += 1;
  return count;
}

/**
 * Soft client filter for fields the public listings API does not yet merge (estrato).
 * Parking is filtered server-side via minParking.
 */
export function listingCasaMapaCoordinates(listing) {
  const latitude = Number(
    listing?.latitude ?? listing?.location?.latitude ?? listing?.lat
  );
  const longitude = Number(
    listing?.longitude ?? listing?.location?.longitude ?? listing?.lng
  );
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }
  return { latitude, longitude };
}

export function listingWithinCasaMapaBounds(listing, bounds) {
  const parsed = parseCasaMapaBounds(bounds);
  if (!parsed) {
    return true;
  }
  const coords = listingCasaMapaCoordinates(listing);
  if (!coords) {
    return false;
  }
  return (
    coords.longitude >= parsed.west &&
    coords.longitude <= parsed.east &&
    coords.latitude >= parsed.south &&
    coords.latitude <= parsed.north
  );
}

export function selectCasaMapaViewportCards(
  listings,
  bounds,
  { limit = CASAMAPA_VIEWPORT_CARD_LIMIT } = {}
) {
  const rows = Array.isArray(listings) ? listings : [];
  const cap = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : CASAMAPA_VIEWPORT_CARD_LIMIT;
  const inView = bounds ? rows.filter((row) => listingWithinCasaMapaBounds(row, bounds)) : rows;
  return inView.slice(0, cap);
}

export function applyCasaMapaClientFilters(listings, state = {}) {
  let rows = Array.isArray(listings) ? listings : [];

  const propertyTypes = Array.isArray(state.propertyTypes)
    ? state.propertyTypes.filter(Boolean)
    : [];
  if (propertyTypes.length) {
    const allowed = new Set(propertyTypes);
    rows = rows.filter((listing) => listing?.propertyType && allowed.has(listing.propertyType));
  }

  const municipalities = normalizeCasaMapaMunicipalityList(
    Array.isArray(state.municipalities) && state.municipalities.length
      ? state.municipalities
      : state.municipality
        ? [state.municipality]
        : []
  );
  if (municipalities.length) {
    const allowed = new Set(municipalities);
    rows = rows.filter((listing) => {
      const normalized = normalizeCasaMapaMunicipality(
        listing?.municipality ||
          listing?.location?.municipality?.name ||
          listing?.city
      );
      return normalized && allowed.has(normalized);
    });
  }

  const estratos = Array.isArray(state.estratos) ? state.estratos.map(Number) : [];
  if (estratos.length) {
    rows = rows.filter((listing) => {
      const estrato = listing?.estrato;
      return estrato != null && estratos.includes(Number(estrato));
    });
  }

  return rows;
}
